import React, { useState, useEffect } from 'react';
import { BookOpen, Hash, TrendingUp, Zap, Brain, Calculator, Microscope, Globe } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import { mapSubjectToSystemTerms, COUNTRY_NAMES } from '../../lib/curriculum-systems';

interface ActivityPlanStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

interface Subject {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}

const subjects: Subject[] = [
  {
    id: 'math',
    name: 'Math',
    icon: <Calculator className="w-6 h-6" />,
    color: 'blue',
    description: 'Numbers, problem-solving, and logical thinking'
  },
  {
    id: 'reading',
    name: 'Reading & Language',
    icon: <BookOpen className="w-6 h-6" />,
    color: 'green',
    description: 'Reading comprehension, vocabulary, and writing'
  },
  {
    id: 'science',
    name: 'Science',
    icon: <Microscope className="w-6 h-6" />,
    color: 'purple',
    description: 'Discovery, experiments, and understanding the world'
  },
  {
    id: 'social',
    name: 'Social Studies',
    icon: <Globe className="w-6 h-6" />,
    color: 'orange',
    description: 'History, geography, and cultural awareness'
  },
  {
    id: 'focus',
    name: 'Focus & Attention',
    icon: <Brain className="w-6 h-6" />,
    color: 'pink',
    description: 'Concentration, mindfulness, and self-regulation'
  }
];

const difficultyLevels = [
  { id: 'easy', name: 'Easy', description: 'Simple, confidence-building activities', color: 'green' },
  { id: 'medium', name: 'Just Right', description: 'Appropriately challenging for growth', color: 'blue' },
  { id: 'hard', name: 'Challenge', description: 'Stretch activities for advanced learners', color: 'purple' }
];

export function ActivityPlanStep({ wizardData, onStepComplete }: ActivityPlanStepProps) {
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [activitiesPerSubject, setActivitiesPerSubject] = useState<Record<string, number>>({});
  const [difficulty, setDifficulty] = useState('medium');
  
  const currentChild = wizardData.createdChild || wizardData.selectedChild;
  const curriculumInfo = wizardData.curriculumSelection;

  // Initialize from wizardData
  useEffect(() => {
    if (wizardData.activityPlan) {
      setSelectedSubjects(wizardData.activityPlan.subjects.map(s => s.subject));
      const activitiesMap = wizardData.activityPlan.subjects.reduce((acc, s) => {
        acc[s.subject] = s.count;
        return acc;
      }, {} as Record<string, number>);
      setActivitiesPerSubject(activitiesMap);
      setDifficulty(wizardData.activityPlan.difficulty || 'medium');
    }
  }, [wizardData.activityPlan]);


  const toggleSubject = (subjectId: string) => {
    const newSubjects = selectedSubjects.includes(subjectId)
      ? selectedSubjects.filter(id => id !== subjectId)
      : [...selectedSubjects, subjectId];
    
    setSelectedSubjects(newSubjects);
    
    // Set default activity count for newly selected subjects
    let newActivitiesPerSubject = activitiesPerSubject;
    if (!selectedSubjects.includes(subjectId)) {
      newActivitiesPerSubject = {
        ...activitiesPerSubject,
        [subjectId]: 2
      };
      setActivitiesPerSubject(newActivitiesPerSubject);
    }
    
    // Build the activity plan with the new data immediately
    const subjectPlans = newSubjects.map(id => ({
      subject: id,
      count: newActivitiesPerSubject[id] || 2
    }));

    const totalActivities = subjectPlans.reduce((sum, s) => sum + s.count, 0);

    onStepComplete({
      activityPlan: {
        subjects: subjectPlans,
        totalActivities,
        difficulty
      }
    });
  };

  const updateActivityCount = (subjectId: string, count: number) => {
    const newCount = Math.max(1, Math.min(10, count));
    const newActivitiesPerSubject = {
      ...activitiesPerSubject,
      [subjectId]: newCount
    };
    setActivitiesPerSubject(newActivitiesPerSubject);
    
    // Build the activity plan with the new data immediately
    const subjectPlans = selectedSubjects.map(id => ({
      subject: id,
      count: newActivitiesPerSubject[id] || 2
    }));

    const totalActivities = subjectPlans.reduce((sum, s) => sum + s.count, 0);

    onStepComplete({
      activityPlan: {
        subjects: subjectPlans,
        totalActivities,
        difficulty
      }
    });
  };

  const updateDifficulty = (newDifficulty: string) => {
    setDifficulty(newDifficulty);
    
    // Build the activity plan with the new difficulty immediately
    const subjectPlans = selectedSubjects.map(id => ({
      subject: id,
      count: activitiesPerSubject[id] || 2
    }));

    const totalActivities = subjectPlans.reduce((sum, s) => sum + s.count, 0);

    onStepComplete({
      activityPlan: {
        subjects: subjectPlans,
        totalActivities,
        difficulty: newDifficulty
      }
    });
  };

  const getTotalActivities = () => {
    return selectedSubjects.reduce((sum, subjectId) => {
      return sum + (activitiesPerSubject[subjectId] || 2);
    }, 0);
  };

  const getColorClasses = (color: string, selected: boolean) => {
    const colors = {
      blue: selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300',
      green: selected ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-300',
      purple: selected ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300',
      orange: selected ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-orange-300',
      pink: selected ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-gray-200 hover:border-pink-300'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getDifficultyColorClasses = (color: string, selected: boolean) => {
    const colors = {
      green: selected ? 'border-green-500 bg-green-50 ring-green-200' : 'border-gray-200 hover:border-green-300',
      blue: selected ? 'border-blue-500 bg-blue-50 ring-blue-200' : 'border-gray-200 hover:border-blue-300',
      purple: selected ? 'border-purple-500 bg-purple-50 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Activity Generation Plan
        </h3>
        <p className="text-gray-600">
          Choose what types of activities to generate for {currentChild?.name || 'your child'}
        </p>
        {curriculumInfo && (
          <div className="mt-4 inline-flex items-center px-4 py-2 bg-blue-50 text-blue-800 rounded-full text-sm">
            <Globe className="w-4 h-4 mr-2" />
            <span>Aligned with {COUNTRY_NAMES[curriculumInfo.countryCode]} curriculum</span>
          </div>
        )}
      </div>

      {/* Subject Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
          Subject Areas
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Select the subjects you'd like activities for (choose at least one)
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjects.map((subject) => {
            const isSelected = selectedSubjects.includes(subject.id);
            return (
              <div
                key={subject.id}
                onClick={() => toggleSubject(subject.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  getColorClasses(subject.color, isSelected)
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${
                    isSelected 
                      ? `bg-${subject.color}-100` 
                      : 'bg-gray-100'
                  }`}>
                    {subject.icon}
                  </div>
                  {isSelected && (
                    <div className={`w-6 h-6 bg-${subject.color}-500 rounded-full flex items-center justify-center`}>
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">{subject.name}</h5>
                <p className="text-xs text-gray-600">{subject.description}</p>
                {curriculumInfo && (
                  <p className="text-xs text-blue-600 mt-1">
                    Curriculum: {mapSubjectToSystemTerms(subject.id, curriculumInfo.countryCode).join(', ')}
                  </p>
                )}
                
                {isSelected && (
                  <div className="mt-3 flex items-center space-x-2">
                    <Hash className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Activities:</span>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateActivityCount(subject.id, (activitiesPerSubject[subject.id] || 2) - 1);
                        }}
                        className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-sm"
                      >
                        -
                      </button>
                      <span className="w-8 text-center text-sm font-semibold">
                        {activitiesPerSubject[subject.id] || 2}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateActivityCount(subject.id, (activitiesPerSubject[subject.id] || 2) + 1);
                        }}
                        className="w-6 h-6 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Difficulty Level */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-purple-600" />
          Difficulty Level
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Choose the appropriate challenge level for the activities
        </p>
        
        <div className="grid md:grid-cols-3 gap-4">
          {difficultyLevels.map((level) => {
            const isSelected = difficulty === level.id;
            return (
              <div
                key={level.id}
                onClick={() => updateDifficulty(level.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  getDifficultyColorClasses(level.color, isSelected)
                } ${isSelected ? 'ring-2' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-lg ${
                    isSelected 
                      ? `bg-${level.color}-100` 
                      : 'bg-gray-100'
                  }`}>
                    {level.id === 'easy' && <Zap className="w-5 h-5" />}
                    {level.id === 'medium' && <TrendingUp className="w-5 h-5" />}
                    {level.id === 'hard' && <Brain className="w-5 h-5" />}
                  </div>
                  {isSelected && (
                    <div className={`w-6 h-6 bg-${level.color}-500 rounded-full flex items-center justify-center`}>
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
                <h5 className="font-semibold text-gray-900 mb-1">{level.name}</h5>
                <p className="text-xs text-gray-600">{level.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Generation Summary */}
      {selectedSubjects.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-blue-600" />
            Generation Summary
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Selected Subjects:</h5>
              <div className="space-y-1">
                {selectedSubjects.map(subjectId => {
                  const subject = subjects.find(s => s.id === subjectId);
                  const count = activitiesPerSubject[subjectId] || 2;
                  return (
                    <div key={subjectId} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{subject?.name}</span>
                      <span className="font-semibold text-gray-900">{count} activities</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Generation Details:</h5>
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Total Activities:</span>
                  <span className="font-semibold text-blue-600">{getTotalActivities()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Difficulty:</span>
                  <span className="font-semibold text-purple-600">
                    {difficultyLevels.find(d => d.id === difficulty)?.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Child:</span>
                  <span className="font-semibold text-green-600">{currentChild?.name}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white bg-opacity-60 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              Ready to generate <strong>{getTotalActivities()} personalized activities</strong> for {currentChild?.name}!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}